import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import React, { useState, useEffect } from "react";

import LoginScreen from "./components/LoginScreen";
import OverviewScreen from "./components/OverviewScreen";
import ManagerScreen from "./components/ManagerScreen";
import ProviderScreen from "./components/ProviderScreen";
import UserScreen from "./components/UserScreen";
import TestFlightScreen from "./components/TestFlightScreen";

const PAGE_META = {
  overview:   { title: "Overview",            crumb: "Dashboard",                  sub: "Snapshot of platform health" },
  booking:    { title: "Booking Management",  crumb: "Operations · Bookings",      sub: "Review, assign, and confirm bookings" },
  provider:   { title: "Provider Management", crumb: "People · Providers",         sub: "Manage the verified care network" },
  user:       { title: "User Management",     crumb: "People · Users",             sub: "Audit registered patient accounts" },
  testflight: { title: "TestFlight Testers",  crumb: "Distribution · TestFlight",  sub: "Invite and manage iOS beta testers" },
};

const THEME_KEY = "@uphealth_theme";

function App() {
  const [isLogin, setIsLogin] = useState(false);
  const [key, setKey] = useState("overview");
  const [globalSearch, setGlobalSearch] = useState("");
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === "light" || saved === "dark") return saved;
    return "light";
  });

  useEffect(() => {
    document.title = "UpHealth Administration";
    if (localStorage.getItem("@uphealth_login_token") !== null) {
      setIsLogin(true);
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  const handleLogout = () => {
    localStorage.removeItem("@uphealth_login_token");
    setIsLogin(false);
  };

  if (!isLogin) {
    return <LoginScreen setIsLogin={setIsLogin} theme={theme} toggleTheme={toggleTheme} />;
  }

  const meta = PAGE_META[key] || PAGE_META.overview;

  return (
    <div className="admin-layout">
      {/* ── Sidebar Navigation ── */}
      <div className="sidebar">
        <div className="sidebar-brand">
          <img src={require("./assets/icon-uphealth.png")} alt="UpHealth logo" />
          <div className="sidebar-brand-text">
            iCareSolutions
            <span>Admin Portal</span>
          </div>
        </div>

        <div className="sidebar-nav">
          <div className="sidebar-section">Workspace</div>
          <div
            className={`nav-item ${key === "overview" ? "active" : ""}`}
            onClick={() => setKey("overview")}
          >
            <i className="bi bi-grid-1x2-fill" />
            <span>Overview</span>
          </div>

          <div className="sidebar-section">Operations</div>
          <div
            className={`nav-item ${key === "booking" ? "active" : ""}`}
            onClick={() => setKey("booking")}
          >
            <i className="bi bi-calendar-check" />
            <span>Bookings</span>
          </div>

          <div className="sidebar-section">People</div>
          <div
            className={`nav-item ${key === "provider" ? "active" : ""}`}
            onClick={() => setKey("provider")}
          >
            <i className="bi bi-person-badge" />
            <span>Providers</span>
          </div>
          <div
            className={`nav-item ${key === "user" ? "active" : ""}`}
            onClick={() => setKey("user")}
          >
            <i className="bi bi-people" />
            <span>Users</span>
          </div>

          <div className="sidebar-section">Distribution</div>
          <div
            className={`nav-item ${key === "testflight" ? "active" : ""}`}
            onClick={() => setKey("testflight")}
          >
            <i className="bi bi-apple" />
            <span>TestFlight Testers</span>
          </div>
        </div>

        <div className="sidebar-footer mt-auto">
          <button className="nav-item-logout" onClick={handleLogout}>
            <i className="bi bi-box-arrow-right" />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* ── Main Content Area ── */}
      <div className="main-content">
        <div className="top-header">
          <div className="top-header__title-block">
            <div className="top-header__breadcrumb">
              <i className="bi bi-house" />
              {meta.crumb}
            </div>
            <h2 className="mb-0">{meta.title}</h2>
          </div>

          <div className="top-header__search">
            <i className="bi bi-search top-header__search-icon" />
            <input
              type="text"
              placeholder="Search bookings, providers, users…"
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
            />
            <span className="top-header__search-shortcut">⌘K</span>
          </div>

          <div className="top-header__actions">
            <button
              className="top-header__icon-btn top-header__icon-btn--theme"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-pressed={theme === "light"}
              onClick={toggleTheme}
            >
              <i className={theme === "dark" ? "bi bi-sun" : "bi bi-moon-stars"} />
            </button>
            <button className="top-header__icon-btn" title="Notifications" aria-label="Notifications">
              <i className="bi bi-bell" />
              <span className="top-header__icon-btn-badge">3</span>
            </button>
            <button className="top-header__icon-btn" title="Help" aria-label="Help">
              <i className="bi bi-question-circle" />
            </button>
            <div className="user-profile-chip">
              <div className="user-avatar">AD</div>
              <div>
                <div className="user-info-name">Administrator</div>
                <div className="user-info-role">admin@uphealth.com</div>
              </div>
            </div>
          </div>
        </div>

        <div className="content-area">
          {key === "overview" && <OverviewScreen goToTab={setKey} />}
          {key === "booking"  && <ManagerScreen  globalSearch={globalSearch} />}
          {key === "provider" && <ProviderScreen globalSearch={globalSearch} />}
          {key === "user"     && <UserScreen     globalSearch={globalSearch} />}
          {key === "testflight" && <TestFlightScreen globalSearch={globalSearch} />}
        </div>
      </div>
    </div>
  );
}

export default App;
